export default function Hero() {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/f16851fd5bda10662fc71e6f7c5a92b7.jpg"
          alt="Smart Water Infrastructure"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a2e3d]/90 via-[#0a3d4f]/80 to-[#0a2e3d]/70"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-24 pb-16 md:pt-0 md:pb-0">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left - Text Content */}
          <div className="space-y-6 md:space-y-8 text-center lg:text-left">
            {/* Main Title */}
            <div>
              <h1 className="text-4xl sm:text-5xl md:text-7xl font-black text-white mb-3 md:mb-4 leading-tight">
                광센서<br />
                <span className="text-teal-400">스마트 디지털</span>
                <br />
                수도미터
              </h1>
              <p className="text-lg md:text-xl text-teal-200 font-semibold tracking-wider">
                Optical Sensing Smart Digital Water Meter
              </p>
            </div>

            {/* Key Points */}
            <div className="flex flex-wrap gap-2 md:gap-3 justify-center lg:justify-start">
              <div className="px-3 md:px-4 py-1.5 md:py-2 bg-teal-500/20 backdrop-blur-sm rounded-full border border-teal-400/30">
                <span className="text-teal-100 text-sm md:text-base font-bold whitespace-nowrap">
                  Carbon-free 탄소 제로
                </span>
              </div>
              <div className="px-3 md:px-4 py-1.5 md:py-2 bg-teal-500/20 backdrop-blur-sm rounded-full border border-teal-400/30">
                <span className="text-teal-100 text-sm md:text-base font-bold whitespace-nowrap">
                  Connected 연결
                </span>
              </div>
              <div className="px-3 md:px-4 py-1.5 md:py-2 bg-teal-500/20 backdrop-blur-sm rounded-full border border-teal-400/30">
                <span className="text-teal-100 text-sm md:text-base font-bold whitespace-nowrap">
                  Consumer 소비자
                </span>
              </div>
            </div>

            {/* Description */}
            <p className="text-base md:text-lg text-gray-300 font-medium leading-relaxed max-w-lg mx-auto lg:mx-0">
              3C 핵심 비전으로 광학 센싱 기술과 IoT를 결합한 차세대 스마트 수도미터기를 제조·생산·유통하는 전문 기업
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center gap-3 md:gap-4 justify-center lg:justify-start">
              <button
                onClick={() => scrollToSection('features')}
                className="px-6 md:px-8 py-3 md:py-4 bg-teal-500 text-white text-base md:text-lg font-bold rounded-lg hover:bg-teal-600 transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap w-full sm:w-auto"
              >
                제품 알아보기
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="px-6 md:px-8 py-3 md:py-4 bg-white/10 backdrop-blur-sm text-white text-base md:text-lg font-bold rounded-lg hover:bg-white/20 transition-all duration-300 border-2 border-white/30 cursor-pointer whitespace-nowrap w-full sm:w-auto"
              >
                문의하기
              </button>
            </div>
          </div>

          {/* Right - Product Image */}
          <div className="flex justify-center lg:justify-end mt-4 lg:mt-0">
            <div className="relative">
              {/* Glow Effect */}
              <div className="absolute inset-0 bg-teal-400/20 rounded-full blur-[80px]"></div>
              {/* Product Image */}
              <div className="relative w-[240px] h-[240px] sm:w-[300px] sm:h-[300px] md:w-[420px] md:h-[420px] lg:w-[500px] lg:h-[500px] rounded-3xl overflow-hidden bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm border border-white/10 p-3 sm:p-4 md:p-6 flex flex-col items-center justify-center">
                {/* Logo - Centered at top */}
                <div className="absolute top-3 sm:top-4 md:top-6 left-1/2 -translate-x-1/2 z-10">
                  <img
                    src="https://static.readdy.ai/image/2299103fa86076f2ed06076e2d6cbff8/b0216ecd9824ced9791064a8a701a53c.png"
                    alt="ROBO Asset Grid Logo"
                    className="h-8 sm:h-10 md:h-14 w-auto"
                  />
                </div>
                <img
                  src="https://static.readdy.ai/image/2299103fa86076f2ed06076e2d6cbff8/3bb60e1413457df40dbc6ee39e0447f8.png"
                  alt="광센서 스마트 디지털 수도미터"
                  title="로보에셋그리드 광학센싱 스마트 디지털 수도미터기"
                  className="w-full h-full object-contain drop-shadow-2xl"
                />
              </div>
              {/* Floating Badge */}
              <div className="absolute -bottom-3 sm:-bottom-4 left-1/2 -translate-x-1/2 bg-teal-500 text-white px-3 sm:px-4 md:px-6 py-1.5 sm:py-2 md:py-3 rounded-full shadow-xl">
                <span className="font-bold text-xs sm:text-sm md:text-base whitespace-nowrap">
                  친환경 플라스틱
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:block">
          <button
            onClick={() => scrollToSection('about')}
            className="inline-flex flex-col items-center space-y-2 text-white/70 hover:text-white transition-colors cursor-pointer"
          >
            <span className="text-base font-semibold">Scroll Down</span>
            <i className="ri-arrow-down-line text-2xl animate-bounce"></i>
          </button>
        </div>
      </div>

      {/* Bottom Gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent z-10"></div>
    </section>
  );
}
